import { useMutation } from '@tanstack/react-query'

import { chooseReward, createRun, finishRun, submitQuizStep, unlockTotem } from './api'
import { writeTotemToken } from './totemStorage'

import type { QuestRun, QuizStepMeta, QuizStepSelection } from './types'

type CreateRunInput = {
  email?: string
  registration_number?: string
  info_responses: Record<string, unknown>
  totemToken: string | null
}

export const applyUnlockTotemResult = (
  eventSlug: string,
  questUid: string,
  data: { ok: boolean; unlock_token?: string }
): void => {
  if (data.unlock_token) {
    writeTotemToken(eventSlug, questUid, data.unlock_token)
  }
}

export const useUnlockTotemMutation = (eventSlug: string, questUid: string) =>
  useMutation({
    mutationFn: (pattern: string) => unlockTotem(eventSlug, questUid, pattern),
    onSuccess: data => applyUnlockTotemResult(eventSlug, questUid, data)
  })

export const useCreateRunMutation = (eventSlug: string, questUid: string) =>
  useMutation<QuestRun, unknown, CreateRunInput>({
    mutationFn: ({ totemToken, ...record }) =>
      createRun(eventSlug, questUid, record, totemToken)
  })

export const useQuizStepMutation = (eventSlug: string, questUid: string, runId: number) =>
  useMutation<{ record: QuestRun; quiz_step: QuizStepMeta }, unknown, QuizStepSelection>({
    mutationFn: selection => submitQuizStep(eventSlug, questUid, runId, selection)
  })

export const useFinishRunMutation = (eventSlug: string, questUid: string, runId: number) =>
  useMutation<QuestRun, unknown, void>({
    mutationFn: () => finishRun(eventSlug, questUid, runId)
  })

export const useChooseRewardMutation = (eventSlug: string, questUid: string, runId: number) =>
  useMutation<QuestRun, unknown, number | null>({
    mutationFn: selectedRewardId => chooseReward(eventSlug, questUid, runId, selectedRewardId)
  })
